import { apiClient } from "@shared/lib/api-client";
import { useMutation } from "@tanstack/react-query";
import type { InferRequestType } from "hono";

export const newsletterSignupMutationKey = ["newsletter-signup"] as const;
export const useNewsletterSignupMutation = () => {
	return useMutation({
		mutationKey: newsletterSignupMutationKey,
		mutationFn: async (
			form: InferRequestType<
				typeof apiClient.newsletter.signup.$post
			>["form"],
		) => {
			const response = await apiClient.newsletter.signup.$post({
				form,
			});

			if (!response.ok) {
				throw new Error("Failed to sign up to newsletter");
			}
		},
	});
};

export const contactFormMutationKey = ["contact-form"] as const;
export const useContactFormMutation = () => {
	return useMutation({
		mutationKey: contactFormMutationKey,
		mutationFn: async (
			form: InferRequestType<typeof apiClient.contact.$post>["form"],
		) => {
			const response = await apiClient.contact.$post({
				form,
			});

			if (!response.ok) {
				throw new Error("Failed to send contact form");
			}
		},
	});
};

export interface SocialProofProperty {
	id: string;
	// PERSON properties have no domain
	domain: string | null;
	companyName?: string | null;
	logo?: string | null;
}

export interface SocialProofData {
	featuredProperties: SocialProofProperty[];
	totalCount: number;
}

export async function getSocialProofData(
	subjectType?: "PERSON",
): Promise<SocialProofData> {
	try {
		const response = await (apiClient as any)["public"]["social-proof"].$get(
			subjectType ? { query: { subjectType } } : undefined,
		);

		if (!response.ok) {
			throw new Error("Failed to fetch social proof data");
		}

		const json = await response.json();

		if (!json.success || !json.data) {
			throw new Error("Invalid social proof data response");
		}

		return json.data as SocialProofData;
	} catch (error) {
		console.error("Failed to fetch social proof data:", error);
		// Return empty data as fallback
		return {
			featuredProperties: [],
			totalCount: 0,
		};
	}
}
